import React, { useEffect, useMemo, useState } from 'react';
import { Container, Paper, SimpleLoader } from '@dev/ee-components';
import { useDispatch, useSelector } from 'react-redux';
import { LoadOnboardingStart, getOnboardingStore } from '@/store/onboarding/actions';
import { DEFAULT_OPENED_ONBOARDING_STEP_ID, DEFAULT_OPENED_ONBOARDING_TASK_ID } from '@/services/constants';
import { OnboardingLoadingState } from '@/store/onboarding/types';
import useMobile from '@/services/hooks/useMobile';
import { DPAUpdatedBanner } from '@/services/Layout/DPABanner';
import { OnboardingCard } from './components/OnboardingCard';
import { OnboardingContent, OnboardingWrapper } from './styles';
import { OnboardingWelcome } from './components/OnboardingWelcome';
import { OnboardingFinishedInfo } from './components/OnboardingFinishedInfo';
import { OnboardingIntro } from './components/OnboardingIntro';
import { OnboardingSkip } from './components/OnboardingSkip';
import { useOnboardingGoals } from './hooks/useOnboardingGoals';
import { OnboardingVideogOverlay } from './components/OnboardingVideoOverlay';
import { getFirstNotDoneTaskIndex } from './tools/getFirstNotDoneTaskIndex';
import { useVisibleTasksList } from './hooks/useVisibleTasksList';
import { getFirstNotDoneStepIndex } from './tools/getFirstNotDoneStepIndex';
import { useFinishedOnboarding } from './hooks/useFinishedOnboarding';

export const Onboarding = () => {
    const dispatch = useDispatch();
    const isMobile = useMobile();
    const onboardingGoals = useOnboardingGoals();
    const onboardingTasksList = useVisibleTasksList(onboardingGoals);

    const firstNotDoneTaskIndex = useMemo(() => getFirstNotDoneTaskIndex(onboardingTasksList), [onboardingTasksList]);

    const [openedCardIndex, setOpenedCardIndex] = useState(DEFAULT_OPENED_ONBOARDING_TASK_ID);
    const [openedStepIndex, setOpenedStepIndex] = useState(DEFAULT_OPENED_ONBOARDING_STEP_ID);
    const { loaded } = useSelector(getOnboardingStore);
    const isLoaded = loaded === OnboardingLoadingState.Loaded;
    useEffect(() => {
        dispatch(LoadOnboardingStart());
    }, []);

    useEffect(() => {
        if (isLoaded) {
            setOpenedCardIndex(firstNotDoneTaskIndex);
            const firstNotDoneTask = onboardingTasksList[firstNotDoneTaskIndex];
            const firstNotDoneStepIndex = getFirstNotDoneStepIndex(
                firstNotDoneTask?.steps?.[firstNotDoneTask.activeStepsIndex]
            );
            setOpenedStepIndex(firstNotDoneStepIndex);
        }
    }, [isLoaded]);

    const handleOpenedCard = (cardIndex: number) => {
        setOpenedCardIndex(cardIndex);
        setOpenedStepIndex(DEFAULT_OPENED_ONBOARDING_STEP_ID);
    };
    const isFinishedOnboarding = useFinishedOnboarding();
    return (
        <Container>
            <DPAUpdatedBanner />
            <OnboardingWelcome />
            <Paper>
                <OnboardingContent $isMobile={isMobile}>
                    {!isLoaded ? (
                        <SimpleLoader />
                    ) : (
                        <>
                            {isFinishedOnboarding ? <OnboardingFinishedInfo /> : <OnboardingIntro />}
                            <OnboardingWrapper container>
                                {onboardingTasksList.map((task, index) => (
                                    <OnboardingCard
                                        key={`${index}-${task.title}`}
                                        cardIndex={index}
                                        openedCardIndex={openedCardIndex}
                                        handleOpenedCard={handleOpenedCard}
                                        openedStep={openedCardIndex === index && openedStepIndex}
                                        isFirstNotDoneTask={index === firstNotDoneTaskIndex}
                                        {...task}
                                    />
                                ))}
                            </OnboardingWrapper>
                            <OnboardingSkip />
                        </>
                    )}
                </OnboardingContent>
            </Paper>
            <OnboardingVideogOverlay />
        </Container>
    );
};
